/**
 * Bulk Update — what counts as a staged row.
 *
 * `stagedValues` is keyed by lineItemId, but its value is not one shape. An NDC update and a move
 * to review stage a single scalar (the new NDC, the review reason). A product change stages two
 * fields, so it holds `{ ndc, quantity }` instead. One predicate has to answer "is this row
 * staged?" for both, because the commit count, the green row, the footer tally and what commit()
 * actually writes all read the same map and must agree.
 *
 * Truthiness cannot be that predicate: `{}` is truthy, so a row that was touched and reverted
 * would count as a change, inflate the commit count and send a no-op item.
 */

import { ndcDigits } from './bulkOverrideFilters';
import { completeLines, isRealCalendarDay, rowScheduleProblem } from './fillLaterSchedule';

export const EMPTY_ROW_EDIT = { ndc: '', quantity: '' };

const isFilled = (value) => value !== '' && value !== null && value !== undefined;

/**
 * A dispensing quantity, so only a positive whole number is a real edit. `0`, `-3`, `1.5` and
 * anything unparseable are refused here rather than reaching the payload — `type="number"` and
 * `min` are advisory outside a submitting form and do not stop a typed or pasted value.
 */
export const isStagedQuantity = (value) =>
  isFilled(value) && Number.isInteger(Number(value)) && Number(value) > 0;

export const hasStagedValue = (staged) => {
  if (typeof staged !== 'object' || staged === null) return isFilled(staged);

  /* A fill later row stages a SCHEDULE -- a list of {quantity, scheduledFor} -- so a partly typed
   * line still counts as touched. Checked before the scalar fields because the schedule shape
   * carries neither ndc nor a top-level quantity. */
  if (Array.isArray(staged.splits)) {
    return staged.splits.some((line) => isFilled(line?.quantity) || isFilled(line?.scheduledFor));
  }

  if (isFilled(staged.scheduledFor)) return true;

  return isFilled(staged.ndc) || isStagedQuantity(staged.quantity);
};

/**
 * The product-change payload for one row. Both ndc and quantity are always sent, edited or not --
 * the row's current value stands in when it was not touched -- so the server never has to infer
 * either one. Each is omitted only when neither a staged nor a current value is readable, because
 * an absent field is a better wire value than null or NaN.
 *
 * Note the fallback ndc belongs to the product being moved OFF. Core has to decide whether it
 * reads `ndc` as "set this" (in which case an untouched row must not carry the old one) or as
 * "this is what it has today". Unresolved -- see NOTES.md.
 */
export const productChangeItem = (params) => {
  const { lineItemId, productId, edit, currentNdc, currentQuantity } = params;
  const ndc = edit.ndc || currentNdc;
  const quantity = isStagedQuantity(edit.quantity)
    ? Number(edit.quantity)
    : Number(currentQuantity);

  return {
    lineItemId,
    productId,
    ...(ndc ? { ndc } : {}),
    ...(Number.isFinite(quantity) ? { quantity } : {}),
  };
};

/**
 * Whether a staged row will actually be written. `hasStagedValue` answers the different question
 * of whether the operator put something in the row, and both are needed: the row keeps its editor
 * once touched, but a value equal to what the row already carries must not be struck through,
 * counted, summarised or sent.
 *
 * Core agrees with this -- it reports such a row as alreadyApplied, no write and no audit event --
 * so without it the screen stages a change, strikes the current value through and reports
 * "N orders updated" for work that never happened.
 *
 * Review stages a reason, which has no counterpart on the row to compare against, so its caller
 * passes no currentNdc and every staged reason stands as a change. `isSameProduct` defaults to
 * false so a caller that omits it treats a product change as a real move rather than silently
 * discarding one.
 */
/**
 * Is this a usable split of the row's current quantity?
 *
 * The value is the quantity being SPLIT OFF -- what gets scheduled as the fill later, which is
 * what fill_later_item.split_quantity holds. It has to be a whole number strictly inside the
 * current quantity: splitting zero is nothing, splitting all of it leaves the original empty, and
 * a fraction is not a dispensable count. Refusing all three here means the operator sees it on
 * the row rather than as a per-row error code after committing.
 */
export const isValidSplitQuantity = (value, currentQuantity) => {
  if (!isStagedQuantity(value)) return false;

  const retain = Number(value);
  const current = Number(currentQuantity);

  if (!Number.isFinite(current) || !Number.isInteger(retain)) return false;

  return retain > 0 && retain < current;
};

/** What the original is left with once the split comes off. Null unless the split is usable. */
export const retainedAfterSplit = (value, currentQuantity) =>
  isValidSplitQuantity(value, currentQuantity) ? Number(currentQuantity) - Number(value) : null;

/**
 * The split payload for one row, shaped as the split API takes it: one entry per line item with a
 * `splits` array. Bulk stages a single split per row -- several dates on ONE line item is the
 * patient profile's "Split more", not this screen (POM-6167: in bulk, instead of one at a time) --
 * but the array is the only shape the API accepts, because validateSplitRequest rejects a repeated
 * lineItemId, so a second split could never be a second entry.
 *
 * splitQuantity goes as a STRING, matching the entity and the queue: a numeric column with a
 * declared scale stores the scale, so "20" stays 20 rather than coming back 20.000.
 *
 * Sends the quantity being split OFF, which is what fill_later_item.split_quantity stores; the
 * server reduces the original by it against the row it re-reads under lock, so what the original
 * keeps is never computed from a stale figure here.
 */
export const splitItem = (params) => {
  const { lineItemId, orderId, staged } = params;
  const schedule = typeof staged === 'object' && staged !== null ? staged.splits : null;

  return {
    orderId,
    lineItemId,
    splits: completeLines(schedule).map((line) => ({
      splitQuantity: String(Number(line.quantity)),
      scheduledFor: line.scheduledFor,
    })),
  };
};

export const moveItem = (params) => {
  const { lineItemId, orderId, staged } = params;
  const scheduledFor = typeof staged === 'object' && staged !== null ? staged.scheduledFor : staged;

  return { orderId, lineItemId, scheduledFor };
};

export const isStagedChange = (params) => {
  const {
    staged,
    currentNdc,
    currentQuantity,
    isSameProduct = false,
    isQuantityOnly = false,
    isSplit = false,
    isMove = false,
  } = params;

  if (!hasStagedValue(staged)) return false;

  if (isMove) {
    return isRealCalendarDay(
      typeof staged === 'object' && staged !== null ? staged.scheduledFor : staged
    );
  }

  /* A split stages a schedule, and a row only counts as changed once at least one line has BOTH
   * halves and the whole schedule fits the row. Tested here rather than only at the inputs so a
   * row can never reach the confirm summary, or the payload, carrying a schedule the server would
   * refuse -- the quantity and date rules live in utils/fillLaterSchedule.js beside their
   * server-side counterparts. */
  if (isSplit) {
    const schedule = typeof staged === 'object' && staged !== null ? staged.splits : null;

    if (completeLines(schedule).length === 0) return false;

    return !rowScheduleProblem({ schedule, currentQuantity });
  }

  /* Quantity mode stages the same {ndc, quantity} shape a product change does -- it is what the
   * row's New qty editor writes -- but only the quantity half is ever set, and it is compared
   * numerically rather than through the NDC comparison below. ndcDigits() would strip a decimal
   * point and then compare a quantity against an NDC.
   *
   * A quantity may move in EITHER direction: raising it is a legitimate correction, so the only
   * questions are whether the value is a usable quantity and whether it actually differs. */
  if (isQuantityOnly) {
    const value = typeof staged === 'object' && staged !== null ? staged.quantity : staged;

    return isStagedQuantity(value) && Number(value) !== Number(currentQuantity);
  }

  if (typeof staged === 'object' && staged !== null) {
    if (!isSameProduct) return true;

    return (
      (isFilled(staged.ndc) && ndcDigits(staged.ndc) !== ndcDigits(currentNdc)) ||
      (isStagedQuantity(staged.quantity) && Number(staged.quantity) !== Number(currentQuantity))
    );
  }

  return currentNdc === undefined || ndcDigits(staged) !== ndcDigits(currentNdc);
};

/**
 * The quantity-change payload for one row. Only the new quantity travels -- the line item keeps the
 * product and NDC it already has, so neither is sent and neither can be rewritten by accident.
 *
 * Number() here as well as in the api layer: this is what the confirm summary counts, and a string
 * would compare unequal to the row's numeric quantity in every caller downstream.
 */
export const quantityChangeItem = (params) => {
  const { lineItemId, staged } = params;
  const value = typeof staged === 'object' && staged !== null ? staged.quantity : staged;

  return { lineItemId, quantity: Number(value) };
};

/**
 * What one row's staged value becomes when the apply bar's value is pushed onto the selection.
 *
 * One function rather than a conditional at the call site because the three modes stage three
 * different shapes: a product change merges {ndc, quantity} over whatever the row already had, a
 * quantity update writes only the quantity half of that same shape, and everything else stages a
 * bare scalar. Quantity mode must NOT fall through to bulkValue -- its apply bar has no NDC picker,
 * so bulkValue is always '' there and applying would wipe every staged row.
 */
export const buildBulkStagedValue = (params) => {
  const {
    currentEdit,
    bulkValue,
    bulkQuantity,
    bulkSchedule,
    moveDate,
    isProductChange,
    isQuantityChange,
    isFillLaterSplit,
    isFillLaterMove,
  } = params;

  if (isFillLaterMove) {
    return isRealCalendarDay(moveDate) ? { scheduledFor: moveDate } : currentEdit || {};
  }

  /* Apply stamps the whole schedule onto the row, as its own copy rather than a shared reference,
   * so moving one order to a different day afterwards cannot edit the apply bar or its siblings.
   * An empty schedule leaves the row alone: applying nothing must not clear what is already
   * staged there. */
  if (isFillLaterSplit) {
    const stamped = completeLines(bulkSchedule).map((line) => ({ ...line }));

    if (stamped.length === 0) return currentEdit || { splits: [] };

    return { splits: stamped };
  }

  if (isProductChange) {
    return {
      ...(currentEdit || EMPTY_ROW_EDIT),
      ...(bulkValue ? { ndc: bulkValue } : {}),
      ...(bulkQuantity ? { quantity: bulkQuantity } : {}),
    };
  }

  if (isQuantityChange) {
    return {
      ...(currentEdit || EMPTY_ROW_EDIT),
      ...(bulkQuantity ? { quantity: bulkQuantity } : {}),
    };
  }

  return bulkValue;
};

export const commitItemsFor = (params) => {
  const {
    rows,
    stagedIds,
    stagedValues,
    targetProductId,
    isUnfulfillment,
    isReview,
    isFillLaterSplit,
    isFillLaterMove,
    isQuantityChange,
    isProductChange,
  } = params;
  const rowsById = new Map(rows.map((row) => [row.lineItemId, row]));

  return stagedIds.map((lineItemId) => {
    if (isUnfulfillment) return { lineItemId };

    if (isReview) return { lineItemId, reviewTarget: stagedValues[lineItemId] };

    if (isFillLaterMove) {
      return moveItem({
        lineItemId,
        orderId: rowsById.get(lineItemId)?.orderId,
        staged: stagedValues[lineItemId],
      });
    }

    // Ahead of isQuantityChange: same staged shape, but a split sends retainQuantity.
    if (isFillLaterSplit) {
      return splitItem({
        lineItemId,
        orderId: rowsById.get(lineItemId)?.orderId,
        staged: stagedValues[lineItemId],
      });
    }

    if (isQuantityChange) {
      return quantityChangeItem({ lineItemId, staged: stagedValues[lineItemId] });
    }

    if (isProductChange) {
      return productChangeItem({
        lineItemId,
        productId: targetProductId,
        edit: stagedValues[lineItemId] || EMPTY_ROW_EDIT,
        currentNdc: rowsById.get(lineItemId)?.ndc,
        currentQuantity: rowsById.get(lineItemId)?.quantity,
      });
    }

    return { lineItemId, ndc: stagedValues[lineItemId] };
  });
};
